import { server } from "@/api";

const prefix = 'api/meetings'

/**
 * Create a member-to-member meeting
 * @param {Object} data - Meeting data
 * @param {string} data.user_id - UUID of the user to meet with (required)
 * @param {string} data.title - Meeting title (required, max:255)
 * @param {string} [data.description] - Meeting description (optional)
 * @param {string} data.scheduled_at - ISO 8601 datetime, must be in future (required)
 * @param {number} [data.duration_minutes] - Duration in minutes (optional, min:15, max:480)
 * @param {string} [data.timezone] - Timezone (optional, max:50)
 * @param {string} [data.location] - Meeting location (optional, max:255)
 * @returns {Promise} Created meeting object
 */
export const createMemberToMember = async (data) => {
    return await server().post(prefix + '/member-to-member', data)
}

/**
 * Create a member-to-company meeting
 * @param {Object} data - Meeting data
 * @param {string} data.company_id - UUID of the company to meet with (required)
 * @param {string} data.title - Meeting title (required, max:255)
 * @param {string} [data.description] - Meeting description (optional)
 * @param {string} data.scheduled_at - ISO 8601 datetime, must be in future (required)
 * @param {number} [data.duration_minutes] - Duration in minutes (optional, min:15, max:480)
 * @param {string} [data.timezone] - Timezone (optional, max:50)
 * @param {string} [data.location] - Meeting location (optional, max:255)
 * @returns {Promise} Created meeting object
 */
export const createMemberToCompany = async (data) => {
    return await server().post(prefix + '/member-to-company', data)
}

/**
 * List user's meetings with optional filters and pagination
 * @param {Object} [params] - Query parameters
 * @param {string} [params.status] - Filter by status: 'pending', 'accepted', 'declined', 'cancelled', 'in_progress', 'completed'
 * @param {string} [params.type] - Filter by type: 'member_to_member', 'member_to_company'
 * @param {boolean} [params.upcoming] - Filter for upcoming meetings only (scheduled_at > now AND status in ['pending', 'accepted'])
 * @param {number} [params.page] - Page number for pagination (default: 1)
 * @returns {Promise} Paginated list of meetings
 */
export const paginate = async (params = {}) => {
    return await server().get(prefix, { params })
}

/**
 * Get meeting details by ID
 * @param {string} meetingId - UUID of the meeting
 * @returns {Promise} Meeting details object
 */
export const get = async (meetingId) => {
    return await server().get(prefix + '/' + meetingId)
}

/**
 * Accept a meeting invitation
 * @param {string} meetingId - UUID of the meeting
 * @returns {Promise} Updated meeting object
 */
export const accept = async (meetingId) => {
    return await server().post(prefix + '/' + meetingId + '/accept')
}

/**
 * Decline a meeting invitation
 * @param {string} meetingId - UUID of the meeting
 * @param {Object} [data] - Optional data
 * @param {string} [data.reason] - Reason for declining (optional)
 * @returns {Promise} Updated meeting object
 */
export const decline = async (meetingId, data = {}) => {
    return await server().post(prefix + '/' + meetingId + '/decline', data)
}

/**
 * Start a meeting (organizer only)
 * @param {string} meetingId - UUID of the meeting
 * @returns {Promise} Updated meeting object with room URLs
 */
export const start = async (meetingId) => {
    return await server().post(prefix + '/' + meetingId + '/start')
}

/**
 * Complete a meeting (organizer only)
 * @param {string} meetingId - UUID of the meeting
 * @returns {Promise} Updated meeting object
 */
export const complete = async (meetingId) => {
    return await server().post(prefix + '/' + meetingId + '/complete')
}

const meetingsApi = {
    createMemberToMember,
    createMemberToCompany,
    paginate,
    get,
    accept,
    decline,
    start,
    complete
}

export default meetingsApi;

